import React, { useState, useMemo } from 'react';
import { Circle as XCircle, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, ListFilter as Filter } from 'lucide-react';
import { BarChart, Bar, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Skeleton } from '../ui/skeleton';
import RuleCard from './RuleCard';

const FILTER_OPTIONS = [
  { key: 'all', label: 'All Rules', icon: Filter },
  { key: 'fail', label: 'Fails', icon: XCircle },
  { key: 'warning', label: 'Warnings', icon: AlertTriangle },
  { key: 'pass', label: 'Passes', icon: CheckCircle2 },
];

export default function QuantAuditPanel({ violations = [], loading = false }) {
  const [filter, setFilter] = useState('all');
  const [sortBySeverity, setSortBySeverity] = useState(true);

  const counts = useMemo(() => ({
    fail: violations.filter(v => v.severity === 'fail').length,
    warning: violations.filter(v => v.severity === 'warning').length,
    pass: violations.filter(v => v.severity === 'pass').length,
  }), [violations]);

  const filtered = useMemo(() => {
    const order = { fail: 0, warning: 1, pass: 2 };
    let result = filter === 'all' ? violations : violations.filter(v => v.severity === filter);
    if (sortBySeverity) {
      result = [...result].sort((a, b) => order[a.severity] - order[b.severity]);
    }
    return result;
  }, [violations, filter, sortBySeverity]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full bg-white/5" />
        <div className="bento-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full bg-white/5" />
          ))}
        </div>
      </div>
    );
  }

  const overallScore = violations.length > 0
    ? Math.round((counts.pass / violations.length) * 100)
    : 0;

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="glass-panel rounded-lg p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-white mb-1">Rule Compliance Score</h3>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className={`text-3xl font-bold font-mono ${
                  overallScore >= 70 ? 'text-[#00f5d4]' : overallScore >= 40 ? 'text-[#f5b400]' : 'text-[#ff3b30]'
                }`}>
                  {overallScore}%
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs font-mono">
                <span className="flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5 text-[#ff3b30]" />
                  <span className="text-[#ff3b30] font-bold">{counts.fail}</span>
                  <span className="text-[#8b93a7]">fails</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#f5b400]" />
                  <span className="text-[#f5b400] font-bold">{counts.warning}</span>
                  <span className="text-[#8b93a7]">warns</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#00f5d4]" />
                  <span className="text-[#00f5d4] font-bold">{counts.pass}</span>
                  <span className="text-[#8b93a7]">pass</span>
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-32 h-12">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Fail', value: counts.fail },
                  { name: 'Warn', value: counts.warning },
                  { name: 'Pass', value: counts.pass },
                ]} barSize={16}>
                  <Tooltip
                    content={({ active, payload }) => active && payload?.length ? (
                      <div className="terminal-card px-2 py-1 text-[10px] terminal-font">
                        <span className="text-white">{payload[0].payload.name}: {payload[0].value}</span>
                      </div>
                    ) : null}
                  />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    <Cell fill="#ff3b30" fillOpacity={0.7} />
                    <Cell fill="#f5b400" fillOpacity={0.7} />
                    <Cell fill="#00f5d4" fillOpacity={0.7} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="wk-confidence-bar w-full sm:w-48">
              <div
                className="wk-confidence-fill transition-all duration-700"
                style={{ width: `${overallScore}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          {FILTER_OPTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-mono transition-all duration-200 ${
                filter === key
                  ? 'bg-white/10 text-white border border-white/20'
                  : 'text-[#8b93a7] hover:text-white hover:bg-white/5 border border-transparent'
              }`}
            >
              <Icon className="w-3 h-3" /> {label}
              {key !== 'all' && (
                <span className={`text-[9px] font-bold ${
                  key === 'fail' ? 'text-[#ff3b30]' : key === 'warning' ? 'text-[#f5b400]' : 'text-[#00f5d4]'
                }`}>
                  {counts[key]}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={() => setSortBySeverity(!sortBySeverity)}
          className="text-[10px] font-mono text-[#8b93a7] hover:text-white transition-colors"
        >
          Sort: {sortBySeverity ? 'Severity' : 'Default'}
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#8b93a7] font-mono text-sm">
          {violations.length === 0
            ? 'No audit data available. Run an analysis first.'
            : `No ${filter} rules found.`
          }
        </div>
      ) : (
        <div className="bento-grid">
          {filtered.map((v, i) => (
            <div
              key={v.rule_id}
              className={`animate-fade-in stagger-${Math.min(i + 1, 5)}`}
              style={v.severity === 'fail' ? { gridColumn: 'span 2 / span 2' } : undefined}
            >
              <RuleCard violation={v} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
